import { createElement, useCallback } from 'react';
import { useDialog } from '../../components/Dialog.tsx';
import { api } from '../../lib/api.ts';
import { checkName, type ConflictPolicy, type NameCheck } from '../../lib/queries.ts';
import { UploadConflict, type ConflictChoice } from './UploadConflict.tsx';
import { persist, useUploads, type UploadRequest } from './UploadQueue.tsx';
import { useToast } from '../../components/Toast.tsx';
import { pathOf } from './dropped-entries.ts';

/**
 * Choose files, settle any name clashes, hand them to the upload queue.
 *
 * The clash questions are the only part that needs a person, so they happen
 * here and up front — before a byte is sent, while there is still nothing to
 * undo. Everything after that belongs to the queue, which survives leaving
 * the page.
 *
 * Shared by the Drive's Upload button and by importing workbooks from the
 * New sheet menu, because they are the same act: both send files to the same
 * endpoint, and a duplicate name deserves the same three answers either way.
 */
export function useFileUpload(folderId: string | null) {
  const dialog = useDialog();
  const toast = useToast();
  const uploads = useUploads();

  return useCallback(
    /**
     * @param into Where to put them, overriding the folder being viewed.
     *   Passed when the user has been asked and has answered; left out when
     *   the destination is already stated by the act, as a drop onto a folder
     *   states it.
     */
    async (list: FileList | readonly File[] | null, into?: string | null) => {
      const chosen = list ? Array.from(list) : [];
      if (chosen.length === 0) return;

      const root = into === undefined ? folderId : into;

      /**
       * Say so before anything else happens.
       *
       * Making the folders and checking every name against what is already
       * filed takes a round trip each, and all of it used to run before a
       * single task was queued — so a folder of two hundred scans showed
       * nothing at all for minutes and looked like it had not started.
       */
      uploads.setPreparing({ total: chosen.length, done: 0 });

      try {
        /**
         * Where each file goes.
         *
         * A folder picked with `webkitdirectory` arrives as a flat list, each
         * file carrying the path it had on disk. The directories are created
         * once each, up front, so two hundred scans in one folder cost one
         * round trip rather than two hundred.
         */
        const folderFor = new Map<string, string | null>();
        /** Folders that could not be made yet; their files carry the path to the queue. */
        const pending = new Map<string, readonly string[]>();
        for (const file of chosen) {
          const relative = pathOf(file);
          // The last segment is the filename itself.
          const segments = relative.split('/').slice(0, -1).filter(Boolean);
          const key = segments.join('/');
          if (folderFor.has(key)) continue;

          if (segments.length === 0) {
            folderFor.set(key, root);
            continue;
          }

          try {
            uploads.setPreparing({
              total: chosen.length,
              done: 0,
              label: `Making “${segments[0] ?? key}”…`,
            });
            const made = await persist(
              () =>
                api.post<{ folderId: string | null }>('/api/drive/folders/ensure-path', {
                  segments,
                  parentId: root,
                }),
              (attempt) =>
                uploads.setPreparing({
                  total: chosen.length,
                  done: 0,
                  label: `Making “${segments[0] ?? key}”, try ${attempt}…`,
                }),
            );
            folderFor.set(key, typeof made === 'symbol' ? root : made.folderId);
          } catch {
            /*
             * Not made, even after retrying. Its files are NOT filed in the
             * open folder instead — that is what scattered a client's folder
             * uploads, their contents loose in whatever folder was open. They
             * go to the queue with the path they belong under; it tries to
             * make the folder again when it reaches them, and if it still
             * cannot, each file fails visibly, with Retry.
             */
            pending.set(key, segments);
          }
        }

        const requests: UploadRequest[] = [];

        const keyOf = (file: File) =>
          pathOf(file).split('/').slice(0, -1).filter(Boolean).join('/');
        /** Where each file will land, worked out once. */
        const targetFor = (file: File): string | null => folderFor.get(keyOf(file)) ?? root;

        /**
         * Every name checked, a few at a time.
         *
         * This used to be one round trip per file, awaited in turn, so two
         * hundred scans meant two hundred trips back to back before the first
         * byte moved — the minutes of apparent silence. They do not depend on
         * each other, so they go out in batches; the count is reported as they
         * land, and the panel fills in rather than sitting still.
         *
         * A small pool rather than all at once: two hundred parallel requests
         * would queue in the browser anyway and starve everything else the page
         * wants to do while it happens.
         */
        const checks = new Map<File, NameCheck | null>();
        const POOL = 8;
        let settled = 0;

        for (let start = 0; start < chosen.length; start += POOL) {
          const batch = chosen.slice(start, start + POOL);
          await Promise.all(
            batch.map(async (file) => {
              // A folder still to be made holds nothing to clash with.
              if (pending.has(keyOf(file))) {
                checks.set(file, null);
                return;
              }
              try {
                // Checked against the folder it will actually land in, which for
                // a folder upload is one this run may have just created.
                checks.set(file, await checkName(targetFor(file), file.name));
              } catch {
                // The check is a courtesy. If it fails, upload anyway and let the
                // server refuse the name — the behaviour before it existed.
                checks.set(file, null);
              }
            }),
          );
          settled += batch.length;
          uploads.setPreparing({ total: chosen.length, done: settled });
        }

        // An answer the user chose to apply to everything still queued, so a
        // folder of twenty duplicates asks once rather than twenty times.
        let blanket: ConflictChoice | undefined;

        for (const [index, file] of chosen.entries()) {
          let policy: ConflictPolicy | undefined;
          const target = targetFor(file);

          if (blanket !== undefined) {
            if (blanket === null) continue;
            policy = blanket;
          } else {
            const check = checks.get(file) ?? null;

            if (check?.taken) {
              const choice = await new Promise<{ choice: ConflictChoice; applyToRest: boolean }>(
                (resolve) => {
                  // createElement rather than JSX: this is a .ts file, and the
                  // hook has no other reason to be a component module.
                  dialog.open(
                    (close) =>
                      createElement(UploadConflict, {
                        filename: file.name,
                        check: check as NameCheck,
                        remaining: chosen.length - index - 1,
                        onChoose: (picked, applyToRest) => resolve({ choice: picked, applyToRest }),
                        onClose: close,
                      }),
                    {
                      /*
                       * Escape, or a click outside, means stop.
                       *
                       * Without this the promise was never settled, so the run
                       * stopped here for good: nothing was uploaded, nothing was
                       * reported, and the panel sat on "Preparing upload" until
                       * the page was reloaded, because the line that clears it
                       * is after the loop that never ended.
                       *
                       * Taken as "skip the rest" rather than "skip this one" —
                       * someone dismissing a dialog they did not ask for is
                       * calling off the upload, not stepping through it.
                       */
                      onDismiss: () => resolve({ choice: null, applyToRest: true }),
                    },
                  );
                },
              );

              if (choice.applyToRest) blanket = choice.choice;
              if (choice.choice === null) continue;
              policy = choice.choice;
            }
          }

          const waiting = pending.get(keyOf(file));
          requests.push({
            file,
            folderId: target,
            ...(waiting ? { folderPath: { segments: waiting, parentId: root } } : {}),
            ...(policy ? { onConflict: policy } : {}),
          });
        }

        if (pending.size > 0) {
          toast.error(
            pending.size === 1
              ? 'A folder could not be made yet.'
              : `${pending.size} folders could not be made yet.`,
            'Their files are waiting in the upload queue and will be tried again; nothing has been put anywhere else.',
          );
        }

        uploads.enqueue(requests);
      } finally {
        /*
         * Whatever happened, the panel comes down.
         *
         * It used to be cleared on the one path that reached the end of the
         * function, so anything that stopped short — a dismissed dialog, a
         * request that threw — left "Preparing upload" on screen for good.
         */
        uploads.setPreparing(null);
      }
    },
    [folderId, dialog, uploads, toast],
  );
}
